import {
  AtSign,
  Check,
  Copy,
  ExternalLink,
  FileBarChart,
  History,
  Link2,
  MessageCircle,
  CircleDollarSign,
  Radio,
  Share2,
  Smile,
  Star,
  Target,
  ThumbsUp,
  Trash2,
  Eye,
  type LucideProps,
} from "lucide-react";
import type { ComponentType } from "react";
import { formatDateTime, formatInteger } from "@/shared/utils/format";
import { formatCurrencyCompact, formatCurrencyFull } from "@/shared/utils/format-currency";
import { HighlightedText } from "@/shared/components/HighlightedText";
import type { Span } from "@/shared/utils/highlight";
import {
  formatReach,
  highlightTitle,
  reportRefLabel,
  type CatalogRef,
  type Mention,
  type ReportRef,
  type SiblingBrand,
  publisherLabel,
} from "@/shared/types/mention";

/**
 * Peças que o card da visão por marca (`MentionCard`) e as linhas da visão
 * Publicações compartilham (2026-10-18): saíram da `MentionsPage` pra o
 * card viver em arquivo próprio sem import circular.
 */

type EngagementMetricSlot = {
  key: string;
  value: number | null;
  label: string;
  Icon: ComponentType<LucideProps>;
};
export type EngagementMetric = {
  key: string;
  value: number;
  label: string;
  Icon: ComponentType<LucideProps>;
};

/**
 * Estrutural, não `Mention` inteira: o engajamento é do POST, então a
 * visão de publicações passa uma `Publication` — que não tem
 * classificação. Ver `PlatformSource`, mesmo motivo.
 */
type EngagementSource = Pick<
  Mention,
  "likes" | "comments" | "views" | "reactions" | "shares"
>;

export function engagementMetrics(mention: EngagementSource): EngagementMetric[] {
  const slots: EngagementMetricSlot[] = [
    { key: "likes", value: mention.likes, label: "Curtidas", Icon: ThumbsUp },
    {
      key: "comments",
      value: mention.comments,
      label: "Comentários",
      Icon: MessageCircle,
    },
    { key: "views", value: mention.views, label: "Visualizações", Icon: Eye },
    {
      key: "reactions",
      value: mention.reactions,
      label: "Reações",
      Icon: Smile,
    },
    {
      key: "shares",
      value: mention.shares,
      label: "Compartilhamentos",
      Icon: Share2,
    },
  ];
  return slots.filter(
    (metric): metric is EngagementMetric => metric.value !== null,
  );
}

/**
 * A figura da coluna "Alcance / Valoração" — uma OU outra, nunca as
 * duas.
 *
 * As duas métricas são espelhos: alcance só existe em rede (vem de
 * `follower_count`, que só o template da Stilingue mapeia) e valoração
 * só existe em imprensa. Antes desta entrega a linha de imprensa
 * mostrava um "—" morto no lugar do alcance; agora mostra o que ela de
 * fato tem.
 *
 * O valor aparece compacto e o extenso vai no tooltip e no
 * `aria-label`: é o número que o analista confere contra a planilha da
 * clipadora, então precisa estar acessível sem virar uma coluna larga.
 */
export function ReachOrValue({
  mediaType,
  reach,
  adValue,
  size = 14
}: {
  mediaType: string;
  reach: string;
  adValue: number | null;
  size?: number;
}) {
  if (mediaType === "press") {
    return (
      <span
        className="reach-figure mention-data-tooltip"
        data-tooltip={`Valoração: ${formatCurrencyFull(adValue)}`}
        data-tooltip-position="bottom"
        aria-label={`Valoração: ${formatCurrencyFull(adValue)}`}
      >
        <CircleDollarSign size={size} aria-hidden="true" />
        {formatCurrencyCompact(adValue)}
      </span>
    );
  }
  return (
    <span
      className="reach-figure mention-data-tooltip"
      data-tooltip="Alcance"
      data-tooltip-position="bottom"
      aria-label={`Alcance: ${reach}`}
    >
      <Radio size={size} aria-hidden="true" />
      {reach}
    </span>
  );
}

/**
 * A linha do que a matéria de IMPRENSA tem no lugar do engajamento.
 *
 * Imprensa não tem curtida nem comentário, mas a planilha da clipadora
 * pode trazer o alcance (a audiência do veículo). Quando traz, a linha
 * diz "Alcance N" com o número cheio — é o que o analista confere contra
 * a planilha —; quando não traz (`null`), cai no texto de sempre, e
 * `0` é um valor que a clipadora informou, não ausência. Beta: o total
 * por veículo mora em `core/press_reach.py`; aqui é só a matéria.
 */
export function PressReachLine({
  pressReach,
  className,
  fallback
}: {
  pressReach: number | null;
  className: string;
  fallback: string;
}) {
  if (pressReach === null) {
    return <p className={className}>{fallback}</p>;
  }
  return (
    <p
      className={className}
      title="Alcance informado pela planilha da clipadora"
      data-testid="press-reach"
    >
      Alcance {formatInteger(pressReach)}
    </p>
  );
}

/**
 * O título, clicável ou não.
 *
 * Na visão de PUBLICAÇÕES ele não abre nada, e isso é regra: uma
 * publicação não é uma menção — quem abre é cada marca citada, porque
 * classificação e CENA são por marca. Nas outras duas o clique abre o
 * drawer.
 */
export function PublicationTitle({
  title,
  spans = [],
  onOpen
}: {
  title: string;
  /** Os termos da marca dentro do título — vazio na visão de
   *  publicações, que não é de uma marca só. */
  spans?: Span[];
  onOpen?: () => void;
}) {
  const texto = (
    <strong className="publication-title">
      <HighlightedText text={title} spans={spans} />
    </strong>
  );
  if (!onOpen) return texto;
  return (
    <button type="button" className="publication-title-button" onClick={onOpen}>
      {texto}
    </button>
  );
}

/**
 * A fileira de baixo: os temas (com o grupo), o código da clipadora,
 * em que relatório a menção já saiu, e o link pro conteúdo original.
 *
 * Sem tema classificado, o chip cai no rótulo de ORIGEM (`theme`, que
 * é "Notícias"/"Redes sociais") — inventar "sem tema" ali seria pior
 * que dizer de onde a publicação veio. Na visão de publicações não há
 * tema (ele é por marca), então lá é sempre a origem.
 */
export function PublicationTags({
  theme,
  themes = [],
  code,
  mediaType,
  reports = [],
  otherBrands = [],
  discarded,
  reviewed,
  edited,
  linked,
  competitor,
  link,
  source,
  className,
  showOriginFallback = true
}: {
  /** Sem tema, o chip cai no rótulo de origem ("Redes sociais").
   *  A lista por marca desliga (2026-10-13): o logo da rede já diz isso. */
  showOriginFallback?: boolean;
  theme: string;
  themes?: CatalogRef[];
  code?: string | null;
  /** Texto do carimbo de descarte ("por Fulana · motivo"); só na
   *  lista das descartadas. */
  discarded?: string | null;
  /** Texto do selo de revisão ("Revisada por Fulana"), ou nulo. */
  reviewed?: string | null;
  /** Texto do selo de edição ("Editada por Fulana em …"), ou nulo. */
  edited?: string | null;
  /** Texto do selo de vínculo manual ("Vinculada à mão por Fulana"). */
  linked?: string | null;
  /** O nome do concorrente, quando a menção é dele — senão nulo. */
  competitor?: string | null;
  /** As outras marcas que veem o mesmo post — vira o selo "também em".
   *  Só na visão por marca: a de publicações já agrupa. */
  otherBrands?: SiblingBrand[];
  /** `press` | `network`. O código só aparece em IMPRENSA: é lá que
   *  a clipadora manda um número que a pessoa confere (o "Código da
   *  Notícia" da Fábrica); em rede o `native_id` é um identificador
   *  sintético de 40 caracteres, e mostrá-lo seria ruído. */
  mediaType?: string;
  reports?: ReportRef[];
  link: string | null;
  source: string;
  className: string;
}) {
  const pressCode = mediaType === "press" ? code : null;
  return (
    <div className={className}>
      {discarded && (
        <span className="plain-tag mention-discarded" title={discarded}>
          <Trash2 size={11} aria-hidden="true" />
          Descartada
        </span>
      )}
      {competitor && (
        <span
          className="plain-tag mention-competitor"
          title={`Menção do concorrente ${competitor}, não da empresa`}
        >
          <Target size={11} aria-hidden="true" />
          Concorrente: {competitor}
        </span>
      )}
      {reviewed && (
        <span className="plain-tag mention-reviewed" title={reviewed}>
          <Check size={11} aria-hidden="true" />
          Revisada
        </span>
      )}
      {edited && (
        <span className="plain-tag mention-edited" title={edited}>
          <History size={11} aria-hidden="true" />
          Editada
        </span>
      )}
      {linked && (
        <span className="plain-tag mention-linked" title={linked}>
          <Link2 size={11} aria-hidden="true" />
          Vinculada à mão
        </span>
      )}
      {themes.length ? (
        themes.map(item => (
          <span
            key={item.id}
            className={`plain-tag mention-theme${item.highlight ? " is-highlight" : ""}`}
            title={[
              item.group_name ? `${item.group_name} · ${item.name}` : item.name,
              highlightTitle(item)
            ]
              .filter(Boolean)
              .join(" — ")}
          >
            {item.highlight && <Star size={11} aria-hidden="true" />}
            {item.group_name && (
              <span className="mention-theme-group">{item.group_name}</span>
            )}
            <span className="mention-theme-name">{item.name}</span>
          </span>
        ))
      ) : showOriginFallback ? (
        <span className="plain-tag mention-theme">{theme}</span>
      ) : null}
      {pressCode && (
        <span className="plain-tag mention-code" title="Código da matéria na clipadora">
          Cód. {pressCode}
        </span>
      )}
      {otherBrands.length > 0 && (
        <span
          className="plain-tag mention-sibling"
          title={`A mesma publicação também aparece em: ${otherBrands
            .map(s => (s.isCompetitor ? `${s.brandName} (${s.companyName})` : s.brandName))
            .join(", ")}`}
        >
          <Copy size={11} aria-hidden="true" />
          Também em {otherBrands[0].brandName}
          {otherBrands.length > 1 && ` +${otherBrands.length - 1}`}
        </span>
      )}
      {reports.length > 0 && (
        <span
          className="plain-tag mention-report"
          title={`Já saiu em: ${reports.map(reportRefLabel).join(", ")}`}
        >
          <FileBarChart size={11} aria-hidden="true" />
          {reportRefLabel(reports[0])}
          {reports.length > 1 && ` +${reports.length - 1}`}
        </span>
      )}
      {link && (
        <a
          className="mention-publication-link"
          href={link}
          target="_blank"
          rel="noreferrer"
          aria-label={`Abrir conteúdo de ${source}`}
        >
          <ExternalLink size={13} aria-hidden="true" />
          <span>Abrir conteúdo</span>
        </a>
      )}
    </div>
  );
}

export function EngagementMetricsList({
  metrics,
  chipClassName,
  iconSize = 13,
}: {
  metrics: EngagementMetric[];
  chipClassName: string;
  iconSize?: number;
}) {
  return (
    <>
      {metrics.map((metric) => (
        <span
          key={metric.key}
          className={`${chipClassName} mention-data-tooltip`}
          data-tooltip={metric.label}
          data-tooltip-position="bottom"
          aria-label={`${metric.label}: ${formatReach(metric.value)}`}
        >
          <metric.Icon size={iconSize} aria-hidden="true" />
          {formatReach(metric.value)}
        </span>
      ))}
    </>
  );
}

/** O carimbo de descarte pra tela: quem e o motivo, ou nulo quando a
 *  menção está na tela. */
export function discardLabel(mention: Mention): string | null {
  if (!mention.discardedAt) return null;
  const who = mention.discardedByName ? `por ${mention.discardedByName}` : "";
  const why = mention.discardReason ? ` · ${mention.discardReason}` : "";
  return `Descartada ${who}${why}`.trim();
}

/** O selo de vínculo manual: quem vinculou, ou nulo. */
export function linkLabel(mention: Mention): string | null {
  if (!mention.linkedAt) return null;
  return mention.linkedByName
    ? `Vinculada à mão por ${mention.linkedByName}: a esteira não casou esta publicação com a marca`
    : "Vinculada à mão: a esteira não casou esta publicação com a marca";
}

/** O selo de revisão pra tela: quem revisou, ou nulo. */
export function reviewLabel(mention: Mention): string | null {
  if (!mention.reviewedAt) return null;
  return mention.reviewedByName
    ? `Revisada por ${mention.reviewedByName}`
    : "Revisada";
}

/** O selo de edição: quem mexeu por último e quando, ou nulo. */
export function editLabel(mention: Mention): string | null {
  if (!mention.lastEdit) return null;
  const quando = formatDateTime(new Date(mention.lastEdit.at));
  return mention.lastEdit.byName
    ? `Editada por ${mention.lastEdit.byName} em ${quando}`
    : `Editada em ${quando}`;
}

/**
 * Quem publicou, em cima do texto (2026-10-18): "Nome (@nick)" com link pro
 * perfil quando há, só rede. No card da lista e no foco do Modo revisão —
 * um componente, pra os dois não divergirem. Em imprensa e sem autor não
 * existe (o veículo é a fonte).
 */
export function MentionPublisher({ mention }: { mention: Mention }) {
  if (mention.mediaType === "press") return null;
  const publisher = publisherLabel(mention);
  if (!publisher) return null;
  return mention.authorProfileUrl ? (
    <a
      className="mc-publisher"
      href={mention.authorProfileUrl}
      target="_blank"
      rel="noreferrer"
      title="Abrir o perfil de quem publicou"
    >
      <AtSign size={13} aria-hidden="true" />
      <span>{publisher}</span>
    </a>
  ) : (
    <span className="mc-publisher">
      <AtSign size={13} aria-hidden="true" />
      <span>{publisher}</span>
    </span>
  );
}
